import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import './styles/layout.css'; // Standardized layout system
import './transparent-messages.css'; // Import dedicated transparency styles
import './components/mobile-fixes.css'; // Mobile responsive fixes
import './components/split-screen-fixes.css'; // Split-screen and narrow desktop optimization

// Components
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Header from './components/Header';
import Parts from './components/Parts';
import Shopping from './components/Shopping';
import DeepSeekChat from './components/DeepSeekChat';
import TopHacker from './components/TopHacker';
import LeafletTreasureHunt from './components/LeafletTreasureHunt';
import AdminTaskReports from './components/AdminTaskReports';
import AdminUserManagement from './components/AdminUserManagement';
import AdminPanel from './components/AdminPanel';
import ProfilePage from './components/ProfilePage';
import PasswordReset from './components/PasswordReset';
import EmailVerification from './components/EmailVerification';
import ResendVerification from './components/ResendVerification';
import System from './components/System';
import HowToPlay from './components/HowToPlay';
import FirstLoginModal from './components/FirstLoginModal';

// Auth context
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';

// Force refresh on route changes to always show fresh data
import { apiForceRefresh } from './services/api';

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <Router>
          <div className="App">
            <AppContent />
          </div>
        </Router>
      </NotificationProvider>
    </AuthProvider>
  );
}

// Component to track current route and handle redirects
function RouteTracker() {
  const location = useLocation();
  const { isAuthenticated, saveCurrentRoute } = useAuth();

  useEffect(() => {
    // Save current route when user navigates (only if authenticated)
    if (isAuthenticated && location.pathname && location.pathname !== '/login') {
      saveCurrentRoute(location.pathname);
    }
  }, [location.pathname, isAuthenticated, saveCurrentRoute]);

  return null; // This component doesn't render anything
}

function AppContent() {
  const { isAuthenticated, user, loading, getAndClearIntendedRoute } = useAuth();
  const [initialRedirect, setInitialRedirect] = useState(null);
  const [showFirstLoginModal, setShowFirstLoginModal] = useState(false);

  // Handle initial app load and check for intended route
  useEffect(() => {
    if (!loading && isAuthenticated) {
      // App loaded - getting latest data
      apiForceRefresh();
      
      // Check if there's a saved route to redirect to
      const savedRoute = getAndClearIntendedRoute();
      if (savedRoute) {
        // Redirecting to saved route - reduced logging per user request
        setInitialRedirect(savedRoute);
      }
    }
  }, [isAuthenticated, loading, getAndClearIntendedRoute]);

  // Show first login modal if user has showHowToPlay = true (only on first login, not when navigating to /how-to-play)
  useEffect(() => {
    if (!loading && isAuthenticated && user && user.showHowToPlay === true) {
      // Only show modal if we're not already on the how-to-play page
      const currentPath = window.location.pathname;
      if (currentPath !== '/how-to-play') {
        setShowFirstLoginModal(true);
      }
    }
  }, [isAuthenticated, loading, user]);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <>
      {isAuthenticated && <Header />}
      <RouteTracker />
      <main className="container">
        <Routes>
          <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/system" />} />
          <Route path="/reset-password/:token" element={!isAuthenticated ? <PasswordReset /> : <Navigate to="/system" />} />
          <Route path="/verify-email/:token" element={!isAuthenticated ? <EmailVerification /> : <Navigate to="/system" />} />
          <Route path="/resend-verification" element={!isAuthenticated ? <ResendVerification /> : <Navigate to="/system" />} />
          <Route path="/system" element={isAuthenticated ? <System /> : <Navigate to="/login" />} />
          <Route path="/dashboard" element={<Navigate to="/system" />} />
          <Route path="/hack-the-planet" element={isAuthenticated ? <LeafletTreasureHunt /> : <Navigate to="/login" />} />
          <Route path="/parts" element={isAuthenticated ? <Parts /> : <Navigate to="/login" />} />
          <Route path="/lifestyle" element={isAuthenticated ? <Shopping /> : <Navigate to="/login" />} />
          <Route path="/shopping" element={<Navigate to="/lifestyle" />} />
          <Route path="/chat" element={isAuthenticated ? <DeepSeekChat /> : <Navigate to="/login" />} />
          <Route path="/community" element={isAuthenticated ? <TopHacker /> : <Navigate to="/login" />} />
          <Route path="/top-hacker" element={<Navigate to="/community" />} />
          <Route path="/admin" element={isAuthenticated && user.role === 'admin' ? <AdminPanel /> : <Navigate to="/system" />} />
          <Route path="/admin/reports" element={isAuthenticated && user.role === 'admin' ? <AdminTaskReports /> : <Navigate to="/system" />} />
          <Route path="/admin/users" element={isAuthenticated && user.role === 'admin' ? <AdminUserManagement /> : <Navigate to="/system" />} />
          <Route path="/profile" element={isAuthenticated ? <ProfilePage /> : <Navigate to="/login" />} />
          <Route path="/how-to-play" element={isAuthenticated ? <HowToPlay /> : <Navigate to="/login" />} />
          <Route path="/" element={<Navigate to={
            isAuthenticated 
              ? (initialRedirect || "/system") 
              : "/login"
          } />} />
        </Routes>
      </main>
      
      {/* First login modal */}
      <FirstLoginModal 
        isVisible={showFirstLoginModal} 
        onClose={() => setShowFirstLoginModal(false)} 
      />
    </>
  );
}

export default App;